// Nebim ürün bazlı satış raporu — parse + validate + SHA-256.
// Tamamen istemci tarafı; dosya ArrayBuffer olarak belleğe alınır, hiçbir
// yere yüklenmez.

import * as XLSX from "xlsx";
import type {
  CanonicalProductSalesRow,
  ParsedRow,
  ParseResult,
  RowIssue,
} from "./types";

/** Türkçe karakterleri sadeleştirip küçük harfe çevirir; '(VH)' gibi
 *  ayraçları da tek alanda tutmak için parantezleri işler.
 *  NOT: Türkçe `ı` (U+0131, noktasız i) Unicode NFD'de çözülmez; bu yüzden
 *  NFD'den ÖNCE Türkçe karakterler ASCII'ye manuel eşlenir. Aksi hâlde
 *  "Satış Miktarı" → "sat_s_miktar" olur (ı → _) ve eşleşme kaybolur. */
const TR_ASCII: Record<string, string> = {
  "ı": "i", "İ": "I", "ş": "s", "Ş": "S",
  "ğ": "g", "Ğ": "G", "ü": "u", "Ü": "U",
  "ö": "o", "Ö": "O", "ç": "c", "Ç": "C",
};

export function normalizeHeader(h: string): string {
  return h
    .toString()
    .replace(/[ıİşŞğĞüÜöÖçÇ]/g, (m) => TR_ASCII[m] ?? m)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\./g, "")
    .replace(/\(([^)]+)\)/g, "_$1")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
}

/** 18 kanonik alan — Nebim ürün bazlı satış raporu sütun eşlemesi.
 *  Anahtarlar normalizeHeader çıktısıdır. */
const HEADER_ALIASES: Record<string, string> = {
  urun_kodu: "sku",
  urun_adi: "product_name",
  renk_kodu: "color_code",
  renk_aciklamasi: "color_name",
  barkod: "barcode",
  beden: "size",
  brut_satis_miktari: "gross_qty",
  iade_miktar: "return_qty",
  satis_miktari: "net_qty",
  brut_satis_vh: "gross_sales_vh",
  brut_satis_vd: "gross_sales_vd",
  satis_iadesi_vh: "return_vh",
  satis_iadesi_vd: "return_vd",
  satis_vh: "net_sales_vh",
  satis_vd: "net_sales_vd",
  brut_satis_isk: "gross_discount",
  iade_isk: "return_discount",
  toplam_isk: "total_discount",
};

/** Yalnızca SKU zorunlu. Diğer alanlar ürün bazlı raporda boş olabilir
 *  (örn. renk/beden olmayan ürünler). */
const REQUIRED_CANONICAL = ["sku"];

/** Başlık satırı bulma eşiği — satır içinde en az bu kadar bilinen başlık
 *  eşleşirse başlık kabul edilir. */
const HEADER_MATCH_THRESHOLD = 10;

/** Toplam/alt-toplam/bölüm satırlarını veri olarak alma.
 *  Nebim ürün bazlı raporlarında yaygın örüntüler: "Toplam", "Genel Toplam",
 *  "Ara Toplam", "Ürün Toplamı", "Renk Toplamı", "Grup Toplamı",
 *  "Mağaza Toplamı", "Sayfa Toplamı", "Rapor Toplamı". */
const SUMMARY_PATTERNS = [
  /^toplam/i,
  /^genel\s+toplam/i,
  /^ara\s+toplam/i,
  /\btoplam[ıi]?\s*:?\s*$/i,
  /\btoplam[ıi]\b/i,
  /^grand\s+total/i,
  /^sayfa\s+toplam/i,
  /^rapor\s+toplam/i,
  /^ofis\s*:/i,
  /^office\s*:/i,
];

export async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Tutarlı sayı ayrıştırma — SheetJS raw:false kullanırken aynı sayı Excel'in
 *  biçim koduna bağlı olarak TR ("1.999,09") veya en-US ("1,999.09") olarak
 *  gelebilir. İki biçimi de doğru çözen tek fonksiyon:
 *    - Hem virgül hem nokta varsa: en sağdaki ayırıcı ondalık, diğerleri
 *      binliktir.
 *    - Yalnız virgül varsa: ondalıktır (TR). "1,00" → 1
 *    - Yalnız nokta varsa:
 *        - Birden çok nokta varsa tamamı binliktir. "1.234.567" → 1234567
 *        - Tek nokta ve sağında tam 3 rakam varsa binliktir (TR). "2.199" → 2199
 *        - Aksi hâlde ondalıktır (en-US). "1.00" → 1 ; "1.5" → 1.5
 *    - Hiç ayırıcı yoksa doğrudan sayıya çevrilir.
 *    - Boş/null → null; sayısal olmayan → null.
 */
function toNumber(v: unknown): number | null {
  if (v == null || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  let s = String(v).trim();
  if (!s) return null;
  // Yüzde, boşluk ve TL/₺ gibi para sembollerini at
  s = s.replace(/[%\s₺$€£]/g, "");
  if (!s) return null;
  // Parantezli negatif: "(1.234,56)" → "-1234.56"
  let sign = 1;
  if (/^\(.*\)$/.test(s)) {
    sign = -1;
    s = s.slice(1, -1);
  }
  if (!/[0-9]/.test(s)) return null;

  const hasComma = s.includes(",");
  const dotCount = (s.match(/\./g) || []).length;

  let normalized: string;
  if (hasComma && dotCount > 0) {
    // İkisi de var → en sağdaki ayırıcı ondalık, diğerleri binlik
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");
    if (lastComma > lastDot) {
      // TR biçimi: "1.234,56"
      normalized = s.replace(/\./g, "").replace(",", ".");
    } else {
      // en-US biçimi: "1,234.56"
      normalized = s.replace(/,/g, "");
    }
  } else if (hasComma) {
    // Yalnız virgül → ondalık (TR). Birden fazla virgül → ilk hariç binlik.
    const commaCount = (s.match(/,/g) || []).length;
    if (commaCount > 1) {
      // Tüm virgüller binlik (örn. "1,234,567" → en-US thousands)
      normalized = s.replace(/,/g, "");
    } else {
      normalized = s.replace(",", ".");
    }
  } else if (dotCount > 1) {
    // Birden çok nokta, virgül yok → tamamı binlik
    normalized = s.replace(/\./g, "");
  } else if (dotCount === 1) {
    // Tek nokta: 3 rakam varsa binlik (TR "2.199"), aksi hâlde ondalık
    const dotIdx = s.lastIndexOf(".");
    const digitsAfter = s.length - dotIdx - 1;
    if (digitsAfter === 3) {
      normalized = s.replace(".", "");
    } else {
      normalized = s;
    }
  } else {
    normalized = s;
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? sign * n : null;
}

/** Metin alanları — başta/sondaki boşluk temizlenir, "null" veya boş string → null. */
function toText(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s || s.toLowerCase() === "null") return null;
  return s;
}

/** Bir satırın "başlık satırı" olup olmadığını değerlendir — bilinen
 *  başlıklardan kaç tanesinin eşleştiğini say. */
function scoreHeaderRow(cells: unknown[] | undefined): {
  score: number;
  mapping: Record<number, string>;
} {
  const mapping: Record<number, string> = {};
  let score = 0;
  if (!cells) return { score, mapping };
  cells.forEach((c, idx) => {
    if (c == null || c === "") return;
    const n = normalizeHeader(String(c));
    if (!n) return;
    const canonical = HEADER_ALIASES[n];
    if (canonical) {
      mapping[idx] = canonical;
      score += 1;
    }
  });
  return { score, mapping };
}

/** Satırın tamamen boş olup olmadığını kontrol et. */
function isEmptyRow(cells: unknown[] | undefined): boolean {
  if (!cells || cells.length === 0) return true;
  return cells.every((c) => c == null || String(c).trim() === "");
}

/** Satır toplam/alt-toplam satırı mı?
 *  SUMMARY_PATTERNS örüntüleri satırın İLK 3 metin hücresinde aranır
 *  (Nebim bazen başlığı 2. veya 3. sütunda bırakır). */
function isSummaryRow(cells: unknown[] | undefined): boolean {
  if (!cells) return false;
  for (let i = 0; i < Math.min(3, cells.length); i++) {
    const v = cells[i];
    if (v == null) continue;
    const s = String(v).trim();
    if (!s) continue;
    if (SUMMARY_PATTERNS.some((p) => p.test(s))) return true;
  }
  return false;
}

/**
 * Detect the known Nebim export quirk where SKU values are one column to the
 * right of the SKU header. Require repeated evidence and a blank next header.
 */
export function detectShiftedSkuValueColumn(
  matrix: unknown[][],
  headerIndex: number,
  headerCells: unknown[],
  headerColumn: number,
): number | null {
  const currentHeader = normalizeHeader(String(headerCells[headerColumn] ?? ""));
  const nextHeader = headerCells[headerColumn + 1];
  const nextHeaderText = String(nextHeader ?? "").trim();
  // A merged header is expanded above, so the adjacent header cell may now
  // repeat the same label instead of being blank. Accept only blank or same-label.
  if (nextHeaderText && normalizeHeader(nextHeaderText) !== currentHeader) return null;
  const sampleRows = matrix
    .slice(headerIndex + 1)
    .filter((row) => !isEmptyRow(row) && !isSummaryRow(row))
    .slice(0, 20);
  const shiftedEvidence = sampleRows.filter((row) => {
    const current = row[headerColumn];
    const next = row[headerColumn + 1];
    return (current == null || String(current).trim() === "") &&
      next != null && /^[A-Za-z0-9][A-Za-z0-9._/-]{4,}$/.test(String(next).trim());
  }).length;
  const alignedEvidence = sampleRows.filter((row) => {
    const current = row[headerColumn];
    return current != null && String(current).trim() !== "";
  }).length;
  return shiftedEvidence >= 2 && shiftedEvidence > alignedEvidence
    ? headerColumn + 1
    : null;
}


/** Ürün satış satırı, yalnızca kimlik metni taşıdığı için kabul edilmez.
 * En az bir gerçek satış ölçüsü veya barkod bulunmalıdır; mağaza/şirket
 * açıklaması gibi satırların ürün kodu alanına kaymasını engeller. */
export function isProductSalesRow(parsed: CanonicalProductSalesRow): boolean {
  if (parsed.barcode) return true;
  return [
    parsed.gross_qty, parsed.return_qty, parsed.net_qty,
    parsed.gross_sales_vh, parsed.gross_sales_vd,
    parsed.return_vh, parsed.return_vd,
    parsed.net_sales_vh, parsed.net_sales_vd,
    parsed.gross_discount, parsed.return_discount, parsed.total_discount,
  ].some((value) => typeof value === "number" && Number.isFinite(value));
}

/** Satır tutarlılık doğrulaması — tahmin yapmaz, uyarı üretir. */
export function validateRow(parsed: CanonicalProductSalesRow): RowIssue[] {
  const issues: RowIssue[] = [];
  if (!parsed.sku && !parsed.product_name?.trim() && !parsed.barcode?.trim()) {
    issues.push({
      code: "product_identity_missing",
      message: "Ürün kodu, ürün adı ve barkod alanlarının tamamı boş",
      severity: "error",
    });
  }

  // Nebim raporunda iade miktarı/tutarı genellikle negatif işaretlidir.
  // Negatif iade değerlerini toplarız; pozitif kaynak değerlerini çıkarırız.
  if (
    parsed.gross_qty != null &&
    parsed.return_qty != null &&
    parsed.net_qty != null
  ) {
    const expected = parsed.return_qty < 0
      ? parsed.gross_qty + parsed.return_qty
      : parsed.gross_qty - parsed.return_qty;
    if (Math.abs(expected - parsed.net_qty) > 0.001) {
      issues.push({
        code: "qty_mismatch",
        message: `Satış miktarı uyumsuz: brüt-iade=${expected}, raporda=${parsed.net_qty}`,
        severity: "warning",
      });
    }
  }

  // Tutar tutarlılığı (VH): iade tutarı negatifse brüt satışa eklenir;
  // pozitif işaretle gelen kaynaklarda çıkarılır. İskonto ayrıca düşülür.
  // Toplam İsk. yoksa Brüt İsk. − İade İsk. ile türetilir; o da yoksa 0.
  if (
    parsed.gross_sales_vh != null &&
    parsed.return_vh != null &&
    parsed.net_sales_vh != null
  ) {
    const grossDisc = parsed.gross_discount ?? 0;
    const retDisc = parsed.return_discount ?? 0;
    const totalDisc =
      parsed.total_discount ??
      (parsed.gross_discount != null || parsed.return_discount != null
        ? grossDisc - retDisc
        : 0);
    const signedReturn = parsed.return_vh < 0
      ? parsed.return_vh
      : -parsed.return_vh;
    const expected = parsed.gross_sales_vh + signedReturn - totalDisc;
    const diff = expected - parsed.net_sales_vh;
    if (Math.abs(diff) > 0.015) {
      const fmt = (n: number | null | undefined) =>
        n == null ? "–" : n.toFixed(2);
      issues.push({
        code: "sales_vh_mismatch",
        message:
          `Satış (VH) uyumsuz | Brüt (VH)=${fmt(parsed.gross_sales_vh)}, ` +
          `İade (VH)=${fmt(parsed.return_vh)}, Toplam İsk.=${fmt(parsed.total_discount)} ` +
          `(Brüt İsk.=${fmt(parsed.gross_discount)}, İade İsk.=${fmt(parsed.return_discount)}) ` +
          `→ beklenen=${expected.toFixed(2)}, raporda=${fmt(parsed.net_sales_vh)} ` +
          `(fark ${diff.toFixed(2)})`,
        severity: "warning",
      });
    }
  }

  // Negatif miktar/tutar sıra dışı — ancak iade raporlarında olabilir, uyarı
  if (parsed.gross_qty != null && parsed.gross_qty < 0) {
    issues.push({
      code: "gross_qty_negative",
      message: "Brüt satış miktarı negatif",
      severity: "warning",
    });
  }

  return issues;
}


/** Extract a report-period date from the explicit Nebim report metadata.
 * Supports visible dates like "Başlangıç Tarihi = 6.10.2026" and ISO dates.
 * Returns null when the source report does not include a valid date.
 */
export function extractNebimReportPeriod(matrix: unknown[][]): {
  start: string | null;
  end: string | null;
} {
  const readDate = (kind: "start" | "end"): string | null => {
    const label = kind === "start" ? "baslangic_tarihi" : "bitis_tarihi";
    for (const row of matrix) {
      for (const cell of row ?? []) {
        if (cell == null) continue;
        const sourceText = String(cell).trim();
        if (!sourceText) continue;
        const normalized = normalizeHeader(sourceText);
        if (!normalized.startsWith(label) && !(kind === "start" ? normalized.startsWith("start_date") : normalized.startsWith("end_date"))) continue;

        const iso = sourceText.match(/\b(\d{4})-(\d{1,2})-(\d{1,2})\b/);
        if (iso) {
          const year = Number(iso[1]);
          const month = Number(iso[2]);
          const day = Number(iso[3]);
          const date = new Date(Date.UTC(year, month - 1, day));
          if (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day) {
            return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          }
          return null;
        }

        const tr = sourceText.match(/(\d{1,2})[./-](\d{1,2})[./-](\d{4})\s*$/);
        if (tr) {
          const day = Number(tr[1]);
          const month = Number(tr[2]);
          const year = Number(tr[3]);
          const date = new Date(Date.UTC(year, month - 1, day));
          if (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day) {
            return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          }
          return null;
        }
      }
    }
    return null;
  };
  return { start: readDate("start"), end: readDate("end") };
}

export async function parseNebimProductSalesExcel(
  file: File,
): Promise<ParseResult> {
  const buf = await file.arrayBuffer();
  const file_hash = await sha256Hex(buf);

  const wb = XLSX.read(buf, { type: "array", cellDates: false });
  const firstSheetName = wb.SheetNames[0];
  const emptyResult: ParseResult = {
    file_name: file.name,
    file_hash,
    report_type: "nebim_product_sales",
    header_row_index: null,
    row_count: 0,
    valid_count: 0,
    warn_count: 0,
    error_count: 0,
    header_map: {},
    source_period_start: null,
    source_period_end: null,
    missing_required: REQUIRED_CANONICAL,
    rows: [],
  };
  if (!firstSheetName) return emptyResult;

  const ws = wb.Sheets[firstSheetName];
  // Ham matris — raw:false sayesinde hücreler Excel'de görüldüğü biçimde
  // (string) gelir; barkodların baştaki sıfırları kaybolmaz, Türkçe
  // sayılar "1.999,09" gibi string olarak toNumber'a düşer.
  // blankrows:true: matris satır indeksleri sayfa satır indeksleriyle
  // hizalanır; aksi hâlde '!merges' referansları yanlış satırlara düşer.
  const matrix: unknown[][] = XLSX.utils.sheet_to_json(ws, {
    header: 1,
    defval: null,
    raw: false,
    blankrows: true,
  }) as unknown[][];

  if (matrix.length === 0) return emptyResult;

  // Nebim ürün bazlı raporunda "Ürün Kodu", "Ürün Adı", "Renk Kodu/Açıklaması"
  // bir ürünün beden/barkod satırlarında ÜST hücrede birleştirilmiş olarak
  // tutulur; Excel bunu tüm satırlarda gösterir, dosyada ise sadece sol-üst
  // hücrede saklar. Birleştirmeleri genişleterek Excel'in gösterdiği değeri
  // okuyoruz — bu bir TAHMİN değil, kaynak dosyadaki gerçek değerdir.
  const merges = (ws as any)["!merges"] as
    | Array<{ s: { r: number; c: number }; e: { r: number; c: number } }>
    | undefined;
  if (Array.isArray(merges)) {
    for (const m of merges) {
      const r0 = m.s.r;
      const c0 = m.s.c;
      const r1 = m.e.r;
      const c1 = m.e.c;
      const topRow = matrix[r0];
      if (!topRow) continue;
      const topLeft = topRow[c0];
      if (topLeft == null || String(topLeft).trim() === "") continue;
      for (let r = r0; r <= r1; r++) {
        if (!matrix[r]) matrix[r] = [];
        for (let col = c0; col <= c1; col++) {
          if (r === r0 && col === c0) continue;
          const current = matrix[r][col];
          if (current == null || String(current).trim() === "") {
            matrix[r][col] = topLeft;
          }
        }
      }
    }
  }

  // Report dates come from explicit source metadata, not from upload time.
  const sourcePeriod = extractNebimReportPeriod(matrix);
  emptyResult.source_period_start = sourcePeriod.start;
  emptyResult.source_period_end = sourcePeriod.end;

  // Find the row with the strongest match against known Nebim column names.
  const scanLimit = Math.min(matrix.length, 30);
  let bestIdx = -1;
  let bestScore = 0;
  let bestMapping: Record<number, string> = {};
  for (let i = 0; i < scanLimit; i++) {
    const { score, mapping } = scoreHeaderRow(matrix[i]);
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
      bestMapping = mapping;
    }
  }

  if (bestIdx === -1 || bestScore < HEADER_MATCH_THRESHOLD) {
    return {
      ...emptyResult,
      missing_required: ["sku"],
    };
  }

  const headerCells = matrix[bestIdx];
  const skuHeaderColumn = Object.entries(bestMapping).find(([, canonical]) => canonical === "sku");
  const skuValueColumn = skuHeaderColumn
    ? detectShiftedSkuValueColumn(matrix, bestIdx, headerCells, Number(skuHeaderColumn[0]))
    : null;
  const header_map: Record<string, string> = {};
  for (const [idxStr, canonical] of Object.entries(bestMapping)) {
    const idx = Number(idxStr);
    const label = String(headerCells[idx] ?? "").trim();
    if (label) header_map[label] = canonical;
  }

  const foundCanonical = new Set(Object.values(bestMapping));
  const missing_required = REQUIRED_CANONICAL.filter(
    (c) => !foundCanonical.has(c),
  );

  // Veri satırları: başlıktan sonrası
  const dataRows: ParsedRow[] = [];
  for (let r = bestIdx + 1; r < matrix.length; r++) {
    const cells = matrix[r];
    if (isEmptyRow(cells)) continue;
    if (isSummaryRow(cells)) continue;

    const raw: Record<string, unknown> = {};
    const parsed: CanonicalProductSalesRow = {
      sku: null,
      product_name: null,
      color_code: null,
      color_name: null,
      barcode: null,
      size: null,
      gross_qty: null,
      return_qty: null,
      net_qty: null,
      gross_sales_vh: null,
      gross_sales_vd: null,
      return_vh: null,
      return_vd: null,
      net_sales_vh: null,
      net_sales_vd: null,
      gross_discount: null,
      return_discount: null,
      total_discount: null,
    };

    let hasAnyValue = false;
    for (const [idxStr, canonical] of Object.entries(bestMapping)) {
      const idx = Number(idxStr);
      const valueIdx = canonical === "sku" && skuValueColumn != null ? skuValueColumn : idx;
      const v = cells[valueIdx];
      const label = String(headerCells[idx] ?? "").trim();
      if (label) raw[label] = v;
      if (v != null && String(v).trim() !== "") hasAnyValue = true;
      switch (canonical) {
        case "sku":
        case "product_name":
        case "color_code":
        case "color_name":
        case "barcode":
        case "size":
          (parsed as any)[canonical] = toText(v);
          break;
        default:
          (parsed as any)[canonical] = toNumber(v);
      }
    }
    // Mapped kolonlarda hiçbir değer yoksa satırı atla (dolgu satırı)
    if (!hasAnyValue) continue;

    // Ürün kodu hücresine yanlışlıkla yazılmış mağaza/şirket açıklamaları
    // tek başına ürün satırı değildir. Gerçek satış ölçüsü veya barkod şart.
    if ((!parsed.sku && !parsed.barcode) || !isProductSalesRow(parsed)) continue;

    const issues: RowIssue[] = missing_required.length
      ? [
          {
            code: "columns_missing",
            message: `Zorunlu kanonik alan eksik: ${missing_required.join(", ")}`,
            severity: "error",
          },
        ]
      : validateRow(parsed);

    const hasError = issues.some((x) => x.severity === "error");
    const hasWarn = issues.some((x) => x.severity === "warning");

    dataRows.push({
      row_index: r + 1, // Excel 1-based
      raw,
      parsed,
      status: hasError ? "error" : hasWarn ? "warn" : "ok",
      issues,
    });
  }

  const valid_count = dataRows.filter((r) => r.status === "ok").length;
  const warn_count = dataRows.filter((r) => r.status === "warn").length;
  const error_count = dataRows.filter((r) => r.status === "error").length;

  return {
    file_name: file.name,
    file_hash,
    report_type: "nebim_product_sales",
    header_row_index: bestIdx + 1,
    source_period_start: sourcePeriod.start,
    source_period_end: sourcePeriod.end,
    row_count: dataRows.length,
    valid_count,
    warn_count,
    error_count,
    header_map,
    missing_required,
    rows: dataRows,
  };
}
